import React from 'react';
import ContentIconAtom from '@atoms/icon-atoms/content-icon-atoms';
import styled from '@emotion/styled/macro';
import StyledClearRefinements from '@components/clear-refinements/clear-refinements';
import Translate from '@utils/translation-functions';
import vars from '@variables';
import { useMenu, UseMenuProps } from 'react-instantsearch';

interface CustomUseMenuProps extends UseMenuProps {
  className?: string;
}

const mapIcons = (formatType: string) => {
  switch (formatType) {
    case 'read':
      return 'article';
    case 'watch':
      return 'video';
    case 'listen':
      return 'podcast';
    default:
      console.warn('formatType failed to match any specified formats.');
      return '';
  }
};

const FilterMenu: React.FC<CustomUseMenuProps> = props => {
  const { items, refine, createURL } = useMenu(props);
  const order = { read: 0, watch: 1, listen: 2 };
  items.sort((a, b) => order[a.value] - order[b.value]);
  const className = props.className;

  return (
    <div className={className}>
      <ul role="menu" aria-label="Content type filter menu">
        <li role="none">
          <StyledClearRefinements />
        </li>
        {items.map(item => {
          let disableFilter = false;
          let disableLinkClass = '';
          if (item.isRefined) {
            disableFilter = true;
            disableLinkClass = 'isDisabled';
          }
          return (
            <li key={item.value} role="none">
              <a
                href={createURL(item.value)}
                onClick={event => {
                  event.preventDefault();
                  refine(item.value);
                }}
                aria-disabled={disableFilter}
                className={disableLinkClass}
                role="menuitem"
              >
                <div>
                  <ContentIconAtom
                    contentType={mapIcons(item.label)}
                    backgroundFill="none"
                    className={'custom-Icon'}
                  />
                  {Translate(item.label, true)}
                </div>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const StyledFilterMenu = styled(FilterMenu)`
  color: ${vars.coreDarkGrey};
  font-weight: ${vars.fontWeightRegular};
  display: flex;
  li:last-child {
    margin-right: 0;
  }

  ul {
    list-style: none;
    display: flex;

    button {
      color: ${vars.coreDarkGrey};
      margin: 0;
      font-weight: ${vars.fontWeightRegular};
      width: 6rem;
      height: 7rem;
      padding-bottom: 0.7rem;
      text-align: center;
      border: none;
      border-bottom: 0.4rem solid #2f2f46;
      background-color: #ffffff;
      cursor: pointer;
      &:hover {
        background-color: #f2f2f2;
      }
      &:disabled {
        cursor: auto;
        border-color: #d8d8d8;
        opacity: 0.7;
        &:hover {
          background-color: #ffffff;
        }
        &:focus {
          background-color: none;
        }
      }
    }

    li {
      display: inline-block;
      ${vars.fontSize2}
      color: ${vars.coreDarkGrey};
      margin: 0 1.6rem 0 0;
      font-weight: ${vars.fontWeightRegular};

      .custom-Icon {
        min-width: 5rem;
      }
      a {
        height: 7rem;
        display: block;
        text-transform: capitalize;
        font-weight: ${vars.fontWeightRegular};
        text-decoration: none;
        border-bottom: 0.4rem solid ${vars.emfBlue};
        &:hover {
          background-color: ${vars.greyHavens};
        }
        &:focus {
          background-color: none;
        }
      }
      a[aria-disabled='true'] {
        display: inline-block; /* For IE11/ MS Edge bug */
        pointer-events: none;
      }
      .isDisabled {
        cursor: not-allowed;
        border-color: #d8d8d8;
        opacity: 0.7;
      }
      div {
        font-weight: ${vars.fontWeightRegular};
        font-size: 1.4rem;
        max-width: 6rem;
        padding-bottom: 0.7rem;
        text-align: center;
      }
    }
  }
`;

export default StyledFilterMenu;
